import { useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import { useExplore } from "@/lib/ExploreContext";
import { useDetailOverlay } from "@/lib/DetailOverlayContext";
import { useLang } from "@/lib/i18n";

const labelCls = "text-[9.5px] tracking-[0.14em] uppercase font-black text-[#9CA3AF]";

// How far "nearby" reaches from the sand, and how many places are worth naming.
const RADIUS_KM = 3;
const MAX = 5;

const kmBetween = (aLat, aLng, bLat, bLng) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

/**
 * "While you're here" — the real businesses sitting within a short walk of the
 * sand, nearest first, each one opening its own place card. A beach with nothing
 * close enough is simply left without the section.
 */
export default function BeachNearbyPlaces({ beach }) {
  const { t } = useLang();
  const { allListings = [] } = useExplore() || {};
  const { openListing } = useDetailOverlay();

  const places = useMemo(() => {
    if (!Number.isFinite(beach.lat) || !Number.isFinite(beach.lng)) return [];
    return allListings
      .filter(
        (l) =>
          l.id !== beach.id &&
          l.type !== "Beach" &&
          Number.isFinite(l.latitude) &&
          Number.isFinite(l.longitude)
      )
      .map((l) => ({ listing: l, km: kmBetween(beach.lat, beach.lng, l.latitude, l.longitude) }))
      .filter((p) => p.km <= RADIUS_KM)
      .sort((a, b) => a.km - b.km)
      .slice(0, MAX);
  }, [allListings, beach.id, beach.lat, beach.lng]);

  if (!places.length) return null;

  return (
    <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
      <div className={labelCls}>{t("beach.nearby")}</div>
      <ul className="mt-1 divide-y divide-[#F3F4F6]">
        {places.map(({ listing, km }) => (
          <li key={listing.id}>
            <button
              type="button"
              onClick={() => openListing(listing.id)}
              className="w-full flex items-baseline gap-2 py-1.5 text-left"
            >
              <span className="text-[12.5px] font-bold text-[#111827] truncate hover:underline">
                {listing.name}
              </span>
              <span className="ml-auto text-[11px] font-semibold text-[#9CA3AF] shrink-0">
                {(km * 0.621371).toFixed(1)} mi
              </span>
              <ArrowUpRight className="w-3 h-3 shrink-0 text-[#F97316] translate-y-[1px]" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}